import React from 'react';
import { formatDate } from '../../../common/dateTime';
import Box from '@mui/joy/Box';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import Stack from "@mui/joy/Stack";
import Typography from '@mui/joy/Typography';
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import Button from "@mui/joy/Button";
import Chip from '@mui/joy/Chip';
import IconButton, {iconButtonClasses} from "@mui/joy/IconButton";
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import StoreIcon from '@mui/icons-material/Store';
import PauseIcon from '@mui/icons-material/Pause';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {getPages} from "../../../common/pages/pages";
import {useParams} from "react-router-dom";
import {NotificationService} from "../../../common/notification";
import CircularProgress from "@mui/joy/CircularProgress";
import { userInventoryApi, UserInventoryFilter } from "../usersApi";
import {Game} from "../../../models/games";
import {gamesListApi} from "../../games/gamesApi";
import { InventorySkinInfo, SkinId, SkinStatus, SkinStatusName } from '../../../models/user';
import {formatRubles} from "../../../common/money";
import Link from "@mui/joy/Link";
import SearchIcon from '@mui/icons-material/Search';
import DebounceInput from '../../../components/debounceInput/DebounceInput';

export function UserInventory() {
    const {id} = useParams();
    const [selectedGame, setSelectedGame] = React.useState<number>(440);
    const [skins, setSkins] = React.useState<InventorySkinInfo[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [games, setGames] = React.useState<Game[]>([]);
    const [loadedGames, setLoadedGames] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [filterId, setFilterId] = React.useState('');
    const [filterStatus, setFilterStatus] = React.useState<number>(-1);

    React.useEffect(() => {
        if (id && BigInt(id) > 0) {
            loadGames().catch(console.error);
            loadData(BigInt(id)).catch(console.error);
        }
    }, [page, selectedGame, filterId, filterStatus])

    const loadGames = async () => {
        if (loadedGames) {
            return;
        }

        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedGames(true)
        }
    }

    const buildFilter = (): UserInventoryFilter => {
        const filter: UserInventoryFilter = {};
        const trimmedId = filterId.trim();
        if (trimmedId) {
            filter.ids = [trimmedId];
        }
        if (filterStatus >= 0) {
            filter.statuses = [filterStatus];
        }
        return filter;
    }

    const loadData = async (id: bigint) => {
        setLoading(true);
        try {
            let result = await userInventoryApi(id, selectedGame, page, 50, buildFilter());
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.total / 50));
                setSkins(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false);
        }
    }

    const getStatusIcon = (status: SkinStatus): any => {
        switch(status) { 
            case SkinStatus.OnSold: { 
               return <StoreIcon/>;
            }
            case SkinStatus.Reserved: {
                return <ShoppingCartCheckoutIcon/>;
            }
            case SkinStatus.OnPause: { 
                return <PauseIcon/>;
             } 
            case SkinStatus.None:
            default: { 
                return <></>;
             }
         }
    }

    const getStatusColor = (status: SkinStatus): 'primary' | 'neutral' | 'danger' | 'success' | 'warning' => {
        switch(status) { 
            case SkinStatus.OnSold: { 
               return 'success';
            }
            case SkinStatus.Reserved: {
                return 'danger';
            }
            case SkinStatus.OnPause: { 
                return 'primary';
             } 
            case SkinStatus.None:
            default: { 
                return 'neutral';
             }
         }
    }

    const getStatusDetails = (skin:InventorySkinInfo):string => {
        switch(skin.status) { 
            case SkinStatus.OnSold: { 
               return `В продаже с ` + (skin.onSoldAt ? formatDate(skin.onSoldAt) : '-');
            }
            case SkinStatus.Reserved: { 
                return `В резерве с ` + (skin.reservedAt ? formatDate(skin.reservedAt) : '-');
             } 
            default: { 
               return SkinStatusName[skin.status];
            } 
         }
    }

    return (
        <React.Fragment>
            <Box
                className="SearchAndFilters-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: {xs: 'flex', sm: 'flex'},
                    flexWrap: 'wrap',
                    gap: 1.5,
                    '& > *': {
                        minWidth: {xs: '120px', md: '160px'},
                    },
                }}
            >
                <FormControl size="sm" sx={{
                    justifyContent: 'start',
                    minWidth: {xs: '100%', md: 'auto'},
                }}>
                    <FormLabel>Игра</FormLabel>
                    <Select size="sm"
                            startDecorator={<VideogameAssetIcon/>}
                            placeholder="Игра"
                            value={selectedGame}
                            onChange={(_, value) => {
                                setSelectedGame(value ?? -1);
                                setPage(1);
                            }}>
                        {games.map((p) =>
                            <Option key={p.id} value={p.id}>{p.name}</Option>
                        )}
                    </Select>
                </FormControl>
                <FormControl size="sm" sx={{
                    justifyContent: 'start',
                    minWidth: {xs: '100%', md: 'auto'},
                }}>
                    <FormLabel>Id</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Id"
                        startDecorator={<SearchIcon/>}
                        debounceTimeout={500}
                        handleDebounce={(val) => {
                            setFilterId(val);
                            setPage(1);
                        }}
                    />
                </FormControl>
                <FormControl size="sm" sx={{
                    justifyContent: 'start',
                    minWidth: {xs: '100%', md: 'auto'},
                }}>
                    <FormLabel>Статус</FormLabel>
                    <Select size="sm"
                            placeholder="Все"
                            value={filterStatus}
                            onChange={(_, value) => {
                                setFilterStatus(value ?? -1);
                                setPage(1);
                            }}>
                        <Option value={-1}>Все</Option>
                        <Option value={SkinStatus.None}>{SkinStatusName[SkinStatus.None]}</Option>
                        <Option value={SkinStatus.OnSold}>{SkinStatusName[SkinStatus.OnSold]}</Option>
                        <Option value={SkinStatus.Reserved}>{SkinStatusName[SkinStatus.Reserved]}</Option>
                        <Option value={SkinStatus.OnPause}>{SkinStatusName[SkinStatus.OnPause]}</Option>
                    </Select>
                </FormControl>
            </Box>
            {loading ? <CircularProgress/> :
                <>
                <Sheet
                    className="OrderTableContainer"
                    sx={{
                        display: {xs: 'none', sm: 'initial'},
                        width: '100%',
                        borderRadius: 'sm',
                        flexShrink: 1,
                        overflow: 'auto',
                        height: 'auto',
                        maxHeight: '50dvh'
                    }}
                >
                    <Table
                        aria-labelledby="tableTitle"
                        stickyHeader
                        hoverRow
                        sx={{
                            '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                            '--Table-headerUnderlineThickness': '1px',
                            '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                            '--TableCell-paddingY': '4px',
                            '--TableCell-paddingX': '8px',
                        }}
                    >
                        <thead>
                            <tr>
                                <th style={{width: 100, padding: '12px 6px'}}></th>
                                <th style={{width: 260, padding: '12px 6px'}}>Id</th>
                                <th style={{width: 240, padding: '12px 6px'}}>Имя</th>
                                <th style={{width: 240, padding: '12px 6px'}}>Тип</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Добавлено</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Статус</th>
                                <th style={{width: 120, padding: '12px 6px'}}>В продаже</th>
                                <th style={{width: 120, padding: '12px 6px'}}>В резерве</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Холд</th>
                                <th style={{width: 110, padding: '12px 6px'}}>Цена</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Del.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {skins.map((row, idx) => (
                                <tr key={`${SkinId.assetId(row.skinId)}-${row.hashName ?? 'noname'}-${row.createdAt ?? 'nots'}-${idx}`}>
                                    <td>
                                        {row.image
                                            ? <Box component="img" src={row.image} sx={{ height: "30px", width: "auto" }} />
                                            : <></>}
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{SkinId.assetId(row.skinId)}</Typography>
                                    </td>
                                    <td>
                                        <Stack spacing={.5} direction={'column'}>
                                            <Typography level="body-xs">{row.name}</Typography>
                                            <Typography level="body-xs" textColor={'background.backdrop'}>{row.hashName}</Typography>
                                        </Stack>
                                    </td>
                                    <td>
                                        <Stack spacing={1} direction={'column'}>
                                            <Typography level="body-xs">{row.type}</Typography>
                                            {row.subType
                                            ? <Typography level="body-xs" fontSize={12} textColor={'background.tooltip'}>{row.subType}</Typography>
                                            :<></>}
                                        </Stack>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{formatDate(row.createdAt)}</Typography>
                                    </td>
                                    <td>
                                        <Chip
                                            variant="soft"
                                            size="sm"
                                            startDecorator={getStatusIcon(row.status)}
                                            color={getStatusColor(row.status)}
                                        >
                                            { row.orderId
                                            ? <Link
                                                underline="hover"
                                                href={`/app/orders/${row.orderId}`}
                                                target="_blank"
                                                level="body-xs"
                                                title={`Заказ #${row.orderId}`}
                                            >
                                                {SkinStatusName[row.status]}
                                            </Link>
                                            : <>{SkinStatusName[row.status]}</>
                                            }
                                        </Chip>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.onSoldAt ? formatDate(row.onSoldAt) : '-'}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.reservedAt ? formatDate(row.reservedAt) : '-'}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.onHoldUntil ? formatDate(row.onHoldUntil) : '-'}</Typography>
                                    </td>
                                    <td>
                                        <Stack spacing={1} direction={'column'}>
                                            {row.price
                                            ? <>
                                                <Typography level="body-xs">{formatRubles(row.price)} {'RUB'}</Typography>
                                                {row.steamPrice
                                                    ?<Typography level="body-xs" fontSize={12} textColor={'background.tooltip'}>
                                                        {formatRubles(row.price)} {'RUB'}
                                                    </Typography>
                                                    :<></>}
                                                </>
                                            :<></>}
                                        </Stack>
                                    </td>
                                    <td>
                                        { row.toDelete ? <CheckRoundedIcon/> : <></> }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
                <Box
                    className="Pagination-laptopUp"
                    sx={{
                        pt: 2,
                        gap: 1,
                        [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                        display: {
                            xs: 'none',
                            md: 'flex',
                        },
                    }}
                >
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<KeyboardArrowLeftIcon/>}
                        disabled={page === 1}
                        onClick={() => setPage(state => state - 1)}
                    >
                        Назад
                    </Button>

                    <Box sx={{flex: 1}}/>
                    {getPages(pageCount).map((p, idx) => (
                        <IconButton
                            key={typeof p === 'number' ? `p-${p}` : `p-ellipsis-${idx}`}
                            size="sm"
                            variant={Number(p) ? 'outlined' : 'plain'}
                            color={page === Number(p) ? "primary" : "neutral"}
                            onClick={() => setPage(Number(p))}
                        >
                            {p}
                        </IconButton>
                    ))}
                    <Box sx={{flex: 1}}/>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        endDecorator={<KeyboardArrowRightIcon/>}
                        disabled={page === pageCount}
                        onClick={() => setPage(state => state + 1)}
                    >
                        Дальше
                    </Button>
                </Box>

                <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                    {skins.map((listItem, idx) => (
                        <List key={`${SkinId.assetId(listItem.skinId)}-${listItem.hashName ?? 'noname'}-${listItem.createdAt ?? 'nots'}-m-${idx}`} size="sm" sx={{'--ListItem-paddingX': 0}}>
                            <ListItem
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'start',
                                }}
                            >
                                <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                    <div>
                                        <Typography gutterBottom sx={{fontWeight: 600}}>
                                            #{SkinId.assetId(listItem.skinId)} {listItem.toDelete ? ' (к удалению)' : ''}
                                        </Typography>
                                        <Typography level="body-xs">{`${listItem.name} (${listItem.hashName})`}</Typography>
                                        <Typography level="body-xs">Тип: {`${listItem.type} ${listItem.subType ? ` (${listItem.subType})` : ''}`}</Typography>
                                        {listItem.onHoldUntil
                                            ? <Typography level="body-xs">{`Холд до ` + formatDate(listItem.onHoldUntil)}</Typography>
                                            :<></>}
                                        {listItem.price
                                            ?<Typography level="body-xs">
                                                Цена: {`${formatRubles(listItem.price)} RUB ${listItem.steamPrice
                                                    ? ` (${formatRubles(listItem.steamPrice)} RUB)` : ''}`}</Typography>
                                            :<></>}
                                        <Chip
                                            variant="soft"
                                            size="sm"
                                            startDecorator={getStatusIcon(listItem.status)}
                                            color={getStatusColor(listItem.status)}
                                        >
                                            { listItem.orderId
                                            ? <Link
                                                underline="hover"
                                                href={`/app/orders/${listItem.orderId}`}
                                                target="_blank"
                                                level="body-xs"
                                                title={`Заказ #${listItem.orderId}`}
                                            >
                                                {getStatusDetails(listItem)}
                                            </Link>
                                            : <>{getStatusDetails(listItem)}</>  
                                            }                                          
                                        </Chip>
                                    </div>
                                </ListItemContent>
                                {listItem.image
                                    ? <Box component="img" src={listItem.image} sx={{ height: "30px", width: "auto" }} />
                                    : <></>}
                            </ListItem>
                            <ListDivider/>
                        </List>
                    ))}
                    <Box
                        className="Pagination-mobile"
                        sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                    >
                        <IconButton
                            aria-label="previous page"
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            <KeyboardArrowLeftIcon/>
                        </IconButton>
                        <Typography level="body-sm" sx={{mx: 'auto'}}>
                            Страница {page} из {pageCount}
                        </Typography>
                        <IconButton
                            aria-label="next page"
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            <KeyboardArrowRightIcon/>
                        </IconButton>
                    </Box>
                </Box>
            </>}
        </React.Fragment>
    );
}
